import type { SelectedValues } from '@vp/swan'
import {
  FlexBox,
  SelectionSet,
  SelectionSetInput,
  SelectionSetLabel,
  SelectionSetTileContents,
  SelectionSetTileDescription,
  SelectionSetTileName,
  Typography,
} from '@vp/swan'
import type { Control } from 'react-hook-form'
import { Controller } from 'react-hook-form'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useUserContext } from '@vp/ubik-context'
import { OptionalIndicator } from '@shared/ui/form-label-indicators'
import { ProjectImage } from '@shared/ui/project-image'
import { formatGenericDate, formatRelativeTime } from '@shared/utils/date.util'
import type { ContactChannel } from '../../../../components/page'
import type { IssueDescriptionFields } from '../../../../config/issue-description-fields'
import { useProjects } from '../../../../hooks/use-projects'
import { mergeSelections } from '../../../../utils/order-selection.utils'
import { ProjectSelectionLinks } from './project-selection-links'

/** Maximum number of projects the shopper can reveal. */
const MAX_VISIBLE_PROJECTS = 10
/** Number of projects shown before any "Show older projects" click. */
const INITIAL_VISIBLE_PROJECTS = 3
/** Additional projects revealed per "Show older projects" click. */
const PROJECTS_REVEAL_STEP = 3

interface Props {
  control: Control<IssueDescriptionFields>
  channel: ContactChannel
}

export const ProjectSelectionField = ({ control, channel }: Props) => {
  const { t } = useTranslation()
  const { locale } = useUserContext()
  const { projects } = useProjects()

  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE_PROJECTS)

  if (!projects || projects.length === 0) return null

  const displayProjects = projects.slice(0, MAX_VISIBLE_PROJECTS)
  const visibleProjects = displayProjects.slice(0, visibleCount)
  const allProjectsVisible = visibleCount >= displayProjects.length

  const handleShowMore = () => {
    setVisibleCount((current) => Math.min(current + PROJECTS_REVEAL_STEP, displayProjects.length))
  }

  const visibleIds = visibleProjects.map((p) => p.id)

  return (
    <FlexBox flexDirection="column" gap={2}>
      <Typography fontSkin="body-standard">
        {t('contact_us.page.steps.issue_description.project_item_label')}
        <OptionalIndicator />
      </Typography>

      <Controller
        name="selectedWorkIds"
        control={control}
        render={({ field }) => {
          const selected = Array.isArray(field.value) ? field.value : []
          const selectedValues = Object.fromEntries(visibleIds.map((id) => [id, selected.includes(id)]))

          const handleChange = (values: SelectedValues) => {
            field.onChange(mergeSelections(values, selected, visibleIds))
          }

          return (
            <FlexBox flexDirection="column" data-testid="project-selection-set">
              <SelectionSet skin="tiles-horizontal" variant="multi-select" selectedValues={selectedValues} onSelectedValuesChange={handleChange}>
                {visibleProjects.map((project) => (
                  <SelectionSetInput key={project.id} value={project.id}>
                    <SelectionSetLabel>
                      <ProjectImage project={project} />
                      <SelectionSetTileContents>
                        <SelectionSetTileName fontSkin="title-item" style={{ color: 'var(--sem-color-fg-standard, #000)' }}>
                          {project.name}
                        </SelectionSetTileName>
                        {project.lastEditedDate && (
                          <SelectionSetTileDescription fontSkin="body-small" textColor="standard">
                            {t('contact_us.page.steps.issue_description.project_edited_on')}
                            {formatRelativeTime(new Date(project.lastEditedDate), locale)}
                          </SelectionSetTileDescription>
                        )}
                        {project.createdDate && (
                          <SelectionSetTileDescription fontSkin="body-small" textColor="subtle" style={{ textAlign: 'left' }}>
                            {t('contact_us.page.steps.issue_description.project_created_on')}
                            {formatGenericDate(new Date(project.createdDate), locale)}
                          </SelectionSetTileDescription>
                        )}
                      </SelectionSetTileContents>
                    </SelectionSetLabel>
                  </SelectionSetInput>
                ))}
              </SelectionSet>

              <ProjectSelectionLinks allProjectsVisible={allProjectsVisible} onShowMore={handleShowMore} />

              {allProjectsVisible && channel !== 'callback' && (
                <Typography fontSkin="body-standard-bold" textColor="standard" mt={3}>
                  {t('contact_us.page.steps.issue_description.project_not_found')}
                </Typography>
              )}
            </FlexBox>
          )
        }}
      />
    </FlexBox>
  )
}
